import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { usePrefersReducedMotion } from "./reducedMotion";

/**
 * Fades its children up the first time they scroll into view; shows them at
 * once under reduced motion. `as` picks the wrapper element; other props pass
 * through to it.
 *
 * @param {{ as?: React.ElementType, delay?: number, children?: React.ReactNode, className?: string, [key: string]: any }} props
 */
const Reveal = ({ as: Tag = "div", delay = 0, children, className = "", ...rest }) => {
	const ref = useRef(null);
	const [visible, setVisible] = useState(false);
	const reduced = usePrefersReducedMotion();

	useEffect(() => {
		if (!ref.current) return undefined;
		if (reduced) {
			setVisible(true);
			return undefined;
		}
		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					setVisible(true);
					observer.disconnect();
				}
			},
			{ threshold: 0.15 },
		);
		observer.observe(ref.current);
		return () => observer.disconnect();
	}, [reduced]);

	const animClass = visible ? "animate-line-reveal" : "opacity-0";
	const style = delay > 0 ? { animationDelay: `${delay}ms` } : undefined;

	return (
		<Tag ref={ref} className={`${animClass} ${className}`.trim()} style={style} {...rest}>
			{children}
		</Tag>
	);
};

Reveal.propTypes = {
	as: PropTypes.elementType,
	delay: PropTypes.number,
	children: PropTypes.node,
	className: PropTypes.string,
};

export default Reveal;
